// Shared seat map types and geometry utilities used by both
// the admin SeatMapEditor and the customer-facing SeatMapPicker.

export type SeatIcon =
  | 'chair'
  | 'sofa'
  | 'vip'
  | 'accessible'
  | 'table'
  | 'bench'
  | 'pouf'
  | 'bar_stool'
  | 'standing'
  | 'stage'
  | 'screen'
  | 'door'
  | 'bar'
  | 'wc'
  | 'stairs'
  | 'blocked'
  | 'square';

export type RowType = 'straight' | 'arc' | 'freeform';
export type SeatStatus = 'available' | 'blocked';

export interface SeatDef {
  id: string;
  label: string;
  section: string | null;
  x?: number;
  y?: number;
  rotation: number;
  icon: SeatIcon;
  custom_svg?: string;
  custom_url?: string;
  width: number;
  height: number;
  status: SeatStatus;
}

export interface ArcParams {
  center_x: number;
  center_y: number;
  radius: number;
  start_angle: number;
  end_angle: number;
}

export interface RowDef {
  id: string;
  label: string;
  type: RowType;
  x: number;
  y: number;
  rotation: number;
  seat_spacing: number;
  curvature?: number;  // degrees -180..180; sign controls bend direction, 0 = straight
  keepUpright?: boolean; // when true, seats keep the row's base rotation instead of following the curve
  sector?: string;     // spatial group (e.g. "Parter", "Balkon", "Lewe skrzydło")
  arc?: ArcParams;
  seats: SeatDef[];
}

export interface MapLabel {
  id: string;
  text: string;
  x: number;
  y: number;
  font_size: number;
  color: string;
  bold: boolean;
}

export interface MapShape {
  id: string;
  type: 'rect' | 'ellipse';
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  rx: number;
}

export interface CanvasDef {
  width: number;
  height: number;
  background: string;
  grid_size?: number;
  seat_label_font_size?: number;
  seat_label_color?: string;
}

export interface SeatMapDef {
  canvas: CanvasDef;
  rows: RowDef[];
  labels: MapLabel[];
  shapes: MapShape[];
}

// Computed position for rendering a seat on the SVG canvas.
export interface ComputedSeat {
  seat: SeatDef;
  row: RowDef;
  abs_x: number;   // center X on canvas
  abs_y: number;   // center Y on canvas
  rotation: number; // degrees, total rotation for the seat icon
}

const DEG = Math.PI / 180;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Compute absolute canvas positions for every seat in a row.
 * straight: seats laid out along a rotated line starting at (row.x, row.y)
 * arc:      seats placed along a circular arc, automatically facing the center
 * freeform: seats use their own (x, y, rotation) as absolute canvas coords
 */
export function computeSeatPositions(row: RowDef): ComputedSeat[] {
  const result: ComputedSeat[] = [];
  const seats = row.seats;
  if (!seats.length) return result;

  // Handle curvature on straight rows: convert to arc on-the-fly
  if (row.type === 'straight' && row.curvature && seats.length >= 2) {
    const arc = arcFromCurvature(row.x, row.y, seats.length, row.seat_spacing, row.rotation, row.curvature);
    if (arc) {
      const { center_x, center_y, radius, start_angle, end_angle } = arc;
      const n = seats.length;
      seats.forEach((seat, i) => {
        const t = n === 1 ? 0.5 : i / (n - 1);
        const angleDeg = lerp(start_angle, end_angle, t);
        const angleRad = angleDeg * DEG;
        const abs_x = center_x + radius * Math.sin(angleRad);
        const abs_y = center_y + radius * Math.cos(angleRad);
        // Seats face the center of curvature (e.g. the stage), not radially
        // outward, hence the 180° flip relative to the position angle —
        // unless keepUpright is set, in which case all seats share the row's
        // base rotation while still following the curved path.
        const rotation = row.keepUpright ? row.rotation : angleDeg + 180;
        result.push({ seat, row, abs_x, abs_y, rotation });
      });
      return result;
    }
  }

  if (row.type === 'arc' && row.arc) {
    const { center_x, center_y, radius, start_angle, end_angle } = row.arc;
    const n = seats.length;
    seats.forEach((seat, i) => {
      const t = n === 1 ? 0.5 : i / (n - 1);
      const angleDeg = lerp(start_angle, end_angle, t);
      const angleRad = angleDeg * DEG;
      const abs_x = center_x + radius * Math.sin(angleRad);
      // FIXED: + instead of - so center above canvas places seats on canvas
      const abs_y = center_y + radius * Math.cos(angleRad);
      // Seats face the center of curvature, not radially outward — unless
      // keepUpright is set.
      const rotation = row.keepUpright ? row.rotation : angleDeg + 180;
      result.push({ seat, row, abs_x, abs_y, rotation });
    });
    return result;
  }

  if (row.type === 'freeform') {
    seats.forEach((seat) => {
      result.push({
        seat,
        row,
        abs_x: seat.x ?? 0,
        abs_y: seat.y ?? 0,
        rotation: seat.rotation,
      });
    });
    return result;
  }

  // straight (default)
  const spacing = row.seat_spacing;
  const rowRad = row.rotation * DEG;
  seats.forEach((seat, i) => {
    const dist = i * spacing;
    const abs_x = row.x + dist * Math.cos(rowRad);
    const abs_y = row.y + dist * Math.sin(rowRad);
    result.push({ seat, row, abs_x, abs_y, rotation: row.rotation });
  });
  return result;
}

/** Flatten all seats with their computed positions from the full map. */
export function computeAllSeats(map: SeatMapDef): ComputedSeat[] {
  return map.rows.flatMap(computeSeatPositions);
}

/**
 * A seat is bookable whenever it isn't blocked — including "open" seats
 * (section=null, status="available") whose price/section is chosen by the
 * customer at purchase time. Mirrors `SeatMapDef.bookable_seat_ids()` on
 * the backend (backend/shop/schemas/event.py).
 */
export function isBookableSeat(seat: SeatDef): boolean {
  return seat.status !== 'blocked';
}

/**
 * Compute arc parameters from a straight row + curvature angle.
 * curvature = total arc angle in degrees (how much the row bends).
 * Sign controls bend direction: positive curves "toward the stage" (the
 * original default direction), negative curves the opposite way.
 */
export function arcFromCurvature(
  x: number, y: number,
  n: number, spacing: number,
  rotDeg: number, curvatureDeg: number,
): ArcParams | null {
  if (n < 2 || curvatureDeg === 0) return null;
  const sign = Math.sign(curvatureDeg);
  const absCurv = Math.abs(curvatureDeg);
  const chord = (n - 1) * spacing;
  const halfCurv = (absCurv / 2) * DEG;
  const radius = chord / (2 * Math.sin(halfCurv));
  // Midpoint of the row
  const mx = x + (chord / 2) * Math.cos(rotDeg * DEG);
  const my = y + (chord / 2) * Math.sin(rotDeg * DEG);
  // Perpendicular direction: 90° CW from row (toward "stage" = smaller y for
  // horizontal rows), flipped by `sign` to support bending the other way.
  const perpX = Math.sin(rotDeg * DEG) * sign;
  const perpY = -Math.cos(rotDeg * DEG) * sign;
  const d = radius * Math.cos(halfCurv);
  const cx = mx + perpX * d;
  const cy = my + perpY * d;
  // Arc angles: atan2(dx, dy) where abs = center + R*(sin, cos)
  const startAngle = Math.atan2(x - cx, y - cy) / DEG;
  const lastX = x + (n - 1) * spacing * Math.cos(rotDeg * DEG);
  const lastY = y + (n - 1) * spacing * Math.sin(rotDeg * DEG);
  const endAngle = Math.atan2(lastX - cx, lastY - cy) / DEG;
  return { center_x: cx, center_y: cy, radius, start_angle: startAngle, end_angle: endAngle };
}

/** Default empty seat map. */
export function emptyMap(): SeatMapDef {
  return {
    canvas: { width: 1200, height: 900, background: '#f9fafb', grid_size: 20 },
    rows: [],
    labels: [],
    shapes: [],
  };
}

export const DEFAULT_SEAT_WIDTH = 32;
export const DEFAULT_SEAT_HEIGHT = 32;
export const DEFAULT_SEAT_SPACING = 36;

/** Generate a straight row of seats. */
export function generateStraightRow(opts: {
  id: string;
  label: string;
  count: number;
  x: number;
  y: number;
  rotation?: number;
  spacing?: number;
  section?: string;
  icon?: SeatIcon;
  width?: number;
  height?: number;
}): RowDef {
  const spacing = opts.spacing ?? DEFAULT_SEAT_SPACING;
  const w = opts.width ?? DEFAULT_SEAT_WIDTH;
  const h = opts.height ?? DEFAULT_SEAT_HEIGHT;
  const seats: SeatDef[] = Array.from({ length: opts.count }, (_, i) => ({
    id: `${opts.id}-${i + 1}`,
    label: String(i + 1),
    section: opts.section ?? null,
    rotation: opts.rotation ?? 0,
    icon: opts.icon ?? 'chair',
    width: w,
    height: h,
    status: 'available',
  }));
  return {
    id: opts.id,
    label: opts.label,
    type: 'straight',
    x: opts.x,
    y: opts.y,
    rotation: opts.rotation ?? 0,
    seat_spacing: spacing,
    seats,
  };
}

/** Generate an arc row of seats. */
export function generateArcRow(opts: {
  id: string;
  label: string;
  count: number;
  center_x: number;
  center_y: number;
  radius: number;
  start_angle: number;
  end_angle: number;
  section?: string;
  icon?: SeatIcon;
  width?: number;
  height?: number;
}): RowDef {
  const w = opts.width ?? DEFAULT_SEAT_WIDTH;
  const h = opts.height ?? DEFAULT_SEAT_HEIGHT;
  const seats: SeatDef[] = Array.from({ length: opts.count }, (_, i) => ({
    id: `${opts.id}-${i + 1}`,
    label: String(i + 1),
    section: opts.section ?? null,
    rotation: 0,
    icon: opts.icon ?? 'chair',
    width: w,
    height: h,
    status: 'available',
  }));
  return {
    id: opts.id,
    label: opts.label,
    type: 'arc',
    x: 0,
    y: 0,
    rotation: 0,
    seat_spacing: 0,
    arc: {
      center_x: opts.center_x,
      center_y: opts.center_y,
      radius: opts.radius,
      start_angle: opts.start_angle,
      end_angle: opts.end_angle,
    },
    seats,
  };
}

/** SVG path data for each seat icon (32×32 viewBox centered at 16,16). */
export const SEAT_ICONS: Record<SeatIcon, string> = {
  chair: 'M8 4h16v14H8zM6 18h20v3H6zM10 21v7h3v-7zm9 0v7h3v-7z',
  sofa: 'M4 10h4V20H4zM24 10h4v10h-4zM6 12h20v6H6zM8 18h16v6H8z',
  vip: 'M6 6l4 8h12l4-8H6zm2 10h16v4H8z',
  accessible: 'M16 4a3 3 0 100 6 3 3 0 000-6zM10 12l3 3v9h2v-7h2v7h2V15l3-3h-3l-2 2-2-2h-5z',
  table: 'M4 8h24v4H4zM6 12h4v12H6zm16 0h4v12h-4zM8 22h16v2H8z',
  bench: 'M4 14h24v4H4zM6 18v8h4v-8zm16 0v8h4v-8z',
  pouf: 'M8 8a6 6 0 0116 0v10a6 6 0 01-16 0V8z',
  bar_stool: 'M11 6h10v3H11zM14 9l-2 17h3l1.5-12h3l1.5 12h3L20 9zM13 18h6v2h-6z',
  standing: 'M16 4a3 3 0 100 6 3 3 0 000-6zM12 14v10h2v-6h4v6h2V14h-8z',
  stage: 'M2 10h28v14H2zM6 6h20v3H6z',
  screen: 'M2 4h28v18H2zM12 22l-4 6h16l-4-6',
  door: 'M6 4h20v24H6zM20 15a1.5 1.5 0 100 3 1.5 1.5 0 000-3z',
  bar: 'M4 6h24v6H4zM6 12h20v14H6zM10 16h12v2H10z',
  wc: 'M8 6h16v6H8zM10 12v12h4V12zm8 0v12h4V12z',
  stairs: 'M4 24h6v-5h6v-5h6v-5h6V4H4z',
  blocked: 'M8 8l16 16M24 8L8 24',
  square: '',
};

export function getOrCreateSeatSessionToken(): string {
  if (typeof window === 'undefined') return 'ssr';
  const key = 'stag-seat-session';
  let token = sessionStorage.getItem(key) || localStorage.getItem(key);
  if (!token) {
    token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }
  try {
    sessionStorage.setItem(key, token);
    localStorage.setItem(key, token);
  } catch {}
  return token;
}

